import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { LockResponse } from "api/ApiClient";
import { BASE_API_URL } from "store/Me";
import { parseLastSeenTimestamp } from "utils/parseLastSeenTimestamp";
import { AdminTable } from "components/shared/AdminTable";
import { AdminTablePagination } from "components/shared/AdminTablePagination";
import { Badge } from "components/ui/badge";
import { statusBadgeOutlineClass } from "shared/utils/statusChipColor";

const SITE_LOCKS_QUERY_KEY = "site-locks";
const DEFAULT_PAGE_SIZE = 50;

async function getSiteLocks(
  siteId: string,
  page: number,
  pageSize: number,
): Promise<LockResponse[]> {
  const urlParams = new URLSearchParams();
  urlParams.append("siteId", siteId);
  urlParams.append("excludedDeviceType", "gwe");
  urlParams.append("page", page.toString());
  urlParams.append("pageSize", pageSize.toString());
  const response = await axios.get<LockResponse[]>(
    `${BASE_API_URL}/api/locks?${urlParams.toString()}`,
  );
  return response.data;
}

function useGetSiteLocks(siteId: string, page: number, pageSize: number) {
  return useQuery({
    queryKey: [SITE_LOCKS_QUERY_KEY, siteId, page, pageSize],
    queryFn: () => getSiteLocks(siteId, page, pageSize),
  });
}

export default function SiteLocks({ siteId }: { siteId: string }) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const { data, isLoading } = useGetSiteLocks(siteId, page, pageSize);
  const locks = data ?? [];

  if (isLoading) {
    return (
      <div className="flex min-h-80 items-center justify-center rounded-lg border border-slate-200 bg-white text-sm text-slate-500 shadow-sm">
        Loading locks...
      </div>
    );
  }

  return (
    <AdminTable
      aria-label="Site locks"
      footer={
        <AdminTablePagination
          currentPage={page}
          pageSize={pageSize}
          visibleRowCount={locks.length}
          isLoading={isLoading}
          onNext={() => setPage((currentPage) => currentPage + 1)}
          onPrevious={() => setPage((currentPage) => Math.max(0, currentPage - 1))}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          disablePrevious={page === 0}
          disableNext={locks.length < pageSize}
        />
      }
    >
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">System Lock ID</th>
            <th scope="col">Door Type</th>
            <th scope="col">Status</th>
            <th scope="col">Last Seen</th>
          </tr>
        </thead>
        <tbody>
          {locks.map((lock) => (
            <tr key={lock.id}>
              <td>
                <Link to={`/admin/locks/${lock.id}`}>{lock.name}</Link>
              </td>
              <td>{lock.systemLockId ?? "—"}</td>
              <td>{lock.doorType ?? "—"}</td>
              <td>
                {lock.onlineStatus && (
                  <Badge
                    className={statusBadgeOutlineClass(
                      lock.onlineStatus === "Online" ? "success" : "default",
                    )}
                    variant="outline"
                  >
                    {lock.onlineStatus}
                  </Badge>
                )}
              </td>
              <td>
                {lock.lastSeenTimeStamp
                  ? parseLastSeenTimestamp(lock.lastSeenTimeStamp)?.toLocaleString() ?? "—"
                  : "—"}
              </td>
            </tr>
          ))}
          {locks.length === 0 && (
            <tr>
              <td colSpan={5} className="py-16 text-center text-sm text-slate-500">
                No locks found for this site.
              </td>
            </tr>
          )}
        </tbody>
    </AdminTable>
  );
}

